/* Doctor Dashboard — iter100.179 layout.
 *
 * Visual contract:
 *   • Header style matches `/doctor/cases` + `/doctor/patients` (text-2xl
 *     title, text-xs subtitle, rounded-full sm buttons).
 *   • Apollo cyan-teal palette only — no emerald carryover from the mockup.
 *   • No left-rail accents on any container/card/tile. Cards use full
 *     borders only; tone is conveyed via icon-chip color + numeric color,
 *     not vertical stripes.
 *
 * Layout (top → bottom):
 *   1. Header (greeting + New Case)
 *   2. DashboardAlertStack — setup/profile completeness nudges
 *      (onboarding + business hours). Planner questions live in NeedsMeNow.
 *   3. NeedsMeNow — case actions waiting on the doctor (top-of-page)
 *   4. 3-card nested-metric snapshot strip — Cases (Active + Pending
 *      Review) · Appointments (Today + This Week) · Collections
 *      (Collected Today + Outstanding A/R)
 *   5. 2-col grid:
 *      LEFT  — scans-uploaded · Compliance section ·
 *              Content freshness · Recent activity
 *      RIGHT (sticky, flat) —
 *        Today's Schedule · Invisique invoices · In Production · Quick Actions
 *
 * Notes:
 *   • Today's Collections + Money Owed widgets removed from the rail —
 *     the same numbers live in the snapshot strip (Collected today,
 *     Outstanding A/R), so the rail duplicates were pruned (iter100.181).
 *   • LiveOffersBanner removed from the dashboard entirely — promotions
 *     still surface in the case wizard and on /doctor/offers (iter100.181).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    Plus, Calendar, ArrowRight, Wallet, Receipt,
    Warning, Briefcase, ShareNetwork,
    FilePlus, CalendarPlus,
    Headset, UserPlus,
    HeartStraight, Factory, Sparkle,
} from "@phosphor-icons/react";
import NeedsMeNow from "@/components/doctor/NeedsMeNow";
import PatientNudges from "@/components/doctor/PatientNudges";
import ComplianceDigestCard from "@/components/doctor/ComplianceDigestCard";
import FirstLoginWizard from "@/components/doctor/FirstLoginWizard";
import ScansUploadedNudge from "@/components/dashboard/ScansUploadedNudge";
import DashboardAlertStack from "@/components/doctor/DashboardAlertStack";
import DoctorOnboardingWidget from "@/components/doctor/DoctorOnboardingWidget";
import {
    statusLabel as sharedStatusLabel,
    ACTIVE_TREATMENT_STATUSES as SHARED_ACTIVE,
    WITH_INVISIQUE_STATUSES as SHARED_WITH_TEAM,
    AWAITING_DOCTOR_STATUSES as SHARED_AWAITING_DOCTOR,
} from "@/lib/caseStatus";


function timeAgo(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "—";
    const diffMin = Math.floor((Date.now() - t) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const hrs = Math.floor(diffMin / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function formatTodayLine() {
    return new Date().toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;


/* Small relative-time formatter used by the Compliance snapshot card
   (iter100.184). Mirrors the copy grammar used by EscalationsInbox. */
function relativeIso(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch { return "—"; }
}


/* Statuses that mean a case is currently in the active treatment phase
   (patient wearing aligners). Drives the "Active cases" KPI.
   iter101.5 — Sourced from shared caseStatus.js so this stays in sync
   with the /doctor/cases-pipeline "Active Treatment" stage bucket.
   Previously drifted (had `"delivered"` / `"refinement"` / `"in_retention"`
   which are not real DB statuses) so `approved_pending_mfg` cases and
   others fell through and showed 0 on the dashboard. */
const ACTIVE_CASE_STATUSES = SHARED_ACTIVE;
/* Statuses meaning the case is in the lab/factory pipeline. Drives the
   "In production" widget. */
const IN_PRODUCTION_STATUSES = new Set([
    "assigned_to_production", "in_production", "submitted_to_production", "shipped",
]);
/* Statuses meaning the case is with the Invisique Planning / QA team —
   the doctor has submitted and the ball is in our court. Drives the
   "With Invisique team" sub-metric.
   iter101.5 — Now sourced from shared caseStatus.js so approved-pending-mfg
   (doctor signed off, planner still authoring MFG spec) shows up here
   as it should. Previously missing → dashboard reported 0 for an
   approved case that hadn't started production yet. */
const WITH_TEAM_STATUSES = SHARED_WITH_TEAM;
/* Statuses meaning the case is waiting on the DOCTOR to approve / respond.
   Sourced from shared caseStatus.js. */
const PENDING_REVIEW_STATUSES = SHARED_AWAITING_DOCTOR;


/* iter100.90 — Rolling 7-day window (today → today+6 inclusive)
   matching the /doctor/appointments page's `AppointmentsStatStrip`
   definition of "this week". Prior implementation used ISO
   calendar week (Mon 00:00 → next Mon 00:00) which produced a
   painful UX bug: an appointment on Tuesday Aug 4 showed as
   "This week: 1" on the Appointments page (rolling 7-day)
   but "This week: 0" on the Dashboard (was in the NEXT calendar
   week when today was Sat Aug 1). Also fixes a related IST
   timezone bug — the prior code used `.toISOString().slice(0,10)`
   which converts local dates to UTC and shifts them one day
   earlier in any UTC+ timezone (e.g. India). Now uses local
   Y/M/D components exactly like the /appointments page. */
function currentWeekRange() {
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6);
    return { startISO: fmt(today), endISO: fmt(end) };
}


export default function DoctorDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [appts, setAppts] = useState([]);
    const [cases, setCases] = useState([]);
    /* iter102.05 — Gate `isDay1` on cases actually having loaded, not
       on the initial empty-array state. Without this, returning to the
       Dashboard shows a two-frame flicker on the NeedsMeNow empty state:
       first "Your inbox will populate" (because `realCases=[]` → isDay1=true)
       then "Inbox zero — nothing needs you right now" (once /cases
       resolves and isDay1 flips false). Track a `casesLoaded` flag so
       NeedsMeNow can keep its skeleton up until we know which empty
       state applies. */
    const [casesLoaded, setCasesLoaded] = useState(false);
    const [b2bInvoices, setB2bInvoices] = useState([]);
    /* iter100.184 — CoE-Support summary drives the 4th snapshot strip
       card (Compliance). Same payload as /doctor/coe-support/summary:
       { has_service, open_escalations: {count, newest_created_at, ...},
         coverage: {monitored, total, pct, unmonitored_case_ids} }. The
       4th tile self-hides when the doctor isn't subscribed. */
    const [coeSummary, setCoeSummary] = useState(null);
    /* iter98.99ac (2026-06) — "Today's collections" summary fed by
     * GET /doctor/billing/today-summary. Two stats per the morning-glance
     * card: collected today (₹ + invoice count) vs pending (₹ + open
     * invoice count). Hydrated alongside the other dashboard data. */
    const [todaySummary, setTodaySummary] = useState(null);
    /* Path B Phase 4 — first-login onboarding state.
       iter100.265 — Wizard modal retired. `DoctorOnboardingWidget`
       + the /doctor/profile completeness banner cover the same
       ground without a modal interruption. */
    const [doctorProfile, setDoctorProfile] = useState(null);

    /* iter100.68 — Content-freshness signal (Articles/Case Studies/
       Reviews on the public microsite). Loaded alongside the other
       dashboard payloads. */
    const [contentFreshness, setContentFreshness] = useState(null);

    const load = () => {
        api.get("/appointments/doctor").then((r) => setAppts(r.data)).catch(() => {});
        api.get("/cases")
            .then((r) => setCases(r.data))
            .catch(() => {})
            .finally(() => setCasesLoaded(true));
        api.get("/doctor/provider-invoices").then((r) => setB2bInvoices(r.data)).catch(() => {});
        api.get("/doctor/billing/today-summary")
            .then((r) => setTodaySummary(r.data)).catch(() => {});
        api.get("/doctor/coe-support/summary").then((r) => setCoeSummary(r.data)).catch(() => {});
        api.get("/doctor/content-freshness").then((r) => setContentFreshness(r.data)).catch(() => {});
        api.get("/doctor/profile").then((r) => {
            setDoctorProfile(r.data);
            // iter100.265 — Auto-stamp `onboarded_at` when the essentials
            // are already filled by the admin so downstream analytics /
            // legacy `onboarded_at`-gated code paths still work. The
            // wizard modal was retired — completeness lives on the
            // profile page + dashboard widget now.
            const p = r.data || {};
            const essentialsFilled =
                (p.bio || "").trim().length > 0 &&
                p.consultation_fee != null &&
                Number(p.consultation_fee) > 0 &&
                (p.image || "").trim().length > 0;
            if (!p.onboarded_at && essentialsFilled) {
                // Idempotent — server accepts an empty payload and just
                // stamps onboarded_at.
                api.post("/doctor/onboard", {})
                    .then(() => api.get("/doctor/profile").then((rr) => setDoctorProfile(rr.data)))
                    .catch(() => {});
            }
        }).catch(() => {});
    };
    useEffect(() => {
        load();
         
    }, []);

    /* iter98.91 → iter98.99di — Auto-seed of the sandbox "Demo · Riya
       Sharma" patient on dashboard mount has been DISABLED per user
       direction (2026-02-20). The backend endpoint is still available
       for the explicit "Reset demo" affordance in TourLibrary and for
       the sandbox-link claim flow — but real (non-sandbox) doctors no
       longer get a synthetic patient + case planted in their portal.
       Keeping the import / load() wiring around the effect intact so
       a future toggle can re-enable per-tenant if needed. */

    /* iter100.90 — Timezone-safe local `today` string (Y-M-D). Prior
       impl used `new Date().toISOString().slice(0,10)` which shifts
       the date by up to ±1 day in any non-UTC timezone (e.g. IST
       evening → returns tomorrow, US morning → returns yesterday),
       causing today's appointments to disappear from the Dashboard's
       "Today" count. Mirrors `todayISO()` in Appointments.jsx. */
    const today = (() => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    })();
    const todayAppts = appts.filter((a) => a.date === today);
    const nextTodayAppt = useMemo(() => {
        const future = todayAppts
            .filter((a) => !["cancelled", "no_show", "completed"].includes(a.status))
            .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
        return future[0] || null;
    }, [todayAppts]);

    /* iter98.91 — Stats exclude the sandbox demo case so the doctor's
       practice space doesn't inflate Active Cases / Outstanding A/R /
       In Production rollups. The cases array still includes demo for
       the Aligner Cases list + drawers (where we WANT it visible);
       only the dashboard rollups use the filtered slice. */
    const realCases = useMemo(() => cases.filter((c) => !c.is_demo), [cases]);

    /* Stat strip — 3 nested-metric cards: Cases · Appointments · Collections */
    const activeCasesCount = useMemo(() => {
        return realCases.filter((c) => ACTIVE_CASE_STATUSES.has(c.status)).length;
    }, [realCases]);
    const inProductionCount = useMemo(() => {
        return realCases.filter((c) => IN_PRODUCTION_STATUSES.has(c.status)).length;
    }, [realCases]);
    /* iter100.267 — "With Invisique team" sub-metric. Counts cases the
       doctor has submitted where the ball is in OUR court (intake →
       planning → QA → awaiting production). Prevents a newly submitted
       case from reading as 0/0 across the Cases card. */
    const withTeamCount = useMemo(() => {
        return realCases.filter((c) => WITH_TEAM_STATUSES.has(c.status)).length;
    }, [realCases]);
    /* iter100.179 — "Pending review" sub-metric on the Cases card.
       Counts cases where the ball is in the DOCTOR's court (planner
       delivered a plan / raised a query). Deep-links straight into the
       Doctor Review stage of the pipeline. */
    const pendingReviewCount = useMemo(() => {
        return realCases.filter((c) => PENDING_REVIEW_STATUSES.has(c.status)).length;
    }, [realCases]);
    /* iter100.90 — "This week" sub-metric on the Appointments card.
       Rolling 7-day window (today → today+6 inclusive) matching the
       /doctor/appointments page. Exclude the same terminal statuses
       (cancelled / no_show / rescheduled) so the count agrees. */
    const weekAppts = useMemo(() => {
        const { startISO, endISO } = currentWeekRange();
        return appts.filter((a) => {
            if (!a.date) return false;
            if (["cancelled", "no_show", "rescheduled"].includes(a.status)) return false;
            return a.date >= startISO && a.date <= endISO;
        }).length;
    }, [appts]);

    /* iter101.48 — "Scan visits booked" sub-metric on the Appointments
       card. The doctor had no way to see the total count of scan
       visits they'd booked in one place: it was buried inside the
       Aligner Cases page as `scan_visit_scheduled` status filter. Now
       it lives on the daily-focus Appointments card, deep-linking to
       the pipeline filter for those cases. `realCases` (excludes
       demo) keeps the count honest. */
    const scanVisitsCount = useMemo(() => {
        return realCases.filter((c) => c.status === "scan_visit_scheduled").length;
    }, [realCases]);

    /* Patient outstanding balance per case (computed server-side in
       _enrich_case). Grouped per patient for the rail card; the strip
       just uses the grand total.

       iter100.316 — Skip `draft` cases. Draft is the New-Case wizard's
       holding pen; the quote in there is a *guess*, never billed,
       never a receivable. This produced phantom outstanding-AR numbers
       on doctors who abandoned a draft with a price in it
       ("Inbox zero · ₹3,000 owed across 1 patient" contradiction).
       Lines up with `Patients.jsx` balanceByPatient + backend
       `list_patients_for_doctor` and `_enrich_case`.

       iter100.70 — Also skip `cancelled`. A cancelled case's quote is
       not a receivable — nothing was ever billed. The backend
       `_enrich_case` now zeroes `payments_summary.balance` for both
       statuses so the `bal <= 0` guard below would already trip; the
       explicit status filter is kept for clarity + defence-in-depth
       so future code paths that recompute A/R don't regress. */
    const patientOutstanding = useMemo(() => {
        const byPatient = new Map();
        for (const c of realCases) {
            if (c.status === "draft" || c.status === "cancelled") continue;
            const bal = Number(c?.payments_summary?.balance || 0);
            if (bal <= 0) continue;
            const pid = c.patient_id;
            const entry = byPatient.get(pid) || {
                id: pid, name: c.patient_name || "Patient", balance: 0, cases: 0,
                overdue: false, lastInv: c.payments_summary?.last_invoice || null,
            };
            entry.balance += bal;
            entry.cases += 1;
            byPatient.set(pid, entry);
        }
        const list = [...byPatient.values()].sort((a, b) => b.balance - a.balance);
        return {
            total: list.reduce((s, x) => s + x.balance, 0),
            patients: list,
        };
    }, [realCases]);

    /* Invisique™ invoices summary — for the right-rail card. */
    const invisiqueOwed = useMemo(() => {
        const open = b2bInvoices.filter((i) => ["sent", "partial", "overdue"].includes(i.status));
        const overdue = open.filter((i) => i.status === "overdue");
        return {
            count: open.length,
            overdueCount: overdue.length,
            total: open.reduce((s, i) => s + Number(i.balance || 0), 0),
        };
    }, [b2bInvoices]);

    /* iter100.199 — Day-1 signal. When the doctor has zero real (non-demo)
       cases, several surfaces below (Compliance section, Pipeline rail,
       Money rail) render muted "populate-in-place" empty states instead
       of either collapsing entirely or showing three green "all clear"
       banners. Doctors who have started but happen to have no drift /
       no in-production / no open invoices still see the previous
       null-collapse behaviour — those are meaningful zeros.
       iter100.233 — Day-1 rule refined: a doctor whose ONLY activity is
       one or more unsent DRAFTS is still meaningfully Day-1 from a
       "your inbox will populate" perspective — the practice hasn't
       actually kicked off yet. Filter drafts out so the Day-1 copy
       surfaces until at least one case is submitted.
       iter102.06 — Cache the resolved value in sessionStorage. On
       return-visits the /cases fetch is slow enough that the parent
       flips `isDay1` from a stale true → false halfway through paint;
       gating the child on `casesLoaded` (iter102.05) removed the copy
       flicker but left a ~600ms skeleton behind. Caching lets us seed
       `isDay1` synchronously from the last-known value so repeat visits
       skip the skeleton entirely. Fresh /cases still updates the value
       (and cache) in the background — first-ever visit still shows the
       skeleton once. */
    const isDay1FromCases = casesLoaded
        ? realCases.filter((c) => c.status !== "draft").length === 0
        : null;
    const [isDay1, setIsDay1] = useState(() => {
        try {
            const cached = sessionStorage.getItem("doctor.isDay1");
            if (cached === "1") return true;
            if (cached === "0") return false;
        } catch { /* SSR / sessionStorage disabled */ }
        return null;
    });
    useEffect(() => {
        if (isDay1FromCases === null) return;
        setIsDay1(isDay1FromCases);
        try {
            sessionStorage.setItem("doctor.isDay1", isDay1FromCases ? "1" : "0");
        } catch { /* SSR / sessionStorage disabled */ }
    }, [isDay1FromCases]);

    const fullName = (user?.doctor_name || user?.name || "").trim();
    const cleanName = fullName.replace(/^(dr\.?|doctor)\s+/i, "");
    const firstName = cleanName.split(" ")[0];

    return (
        <div data-testid="doctor-dashboard" className="space-y-5 -mt-2">
            {/* iter100.265 — The DoctorOnboardingDialog wizard modal
                was retired. Completeness lives on the /doctor/profile
                banner + the compact <DoctorOnboardingWidget> chip
                below. Doctors are no longer interrupted by a modal on
                every fresh login. */}

            {/* iter100.315 — First-Login Wizard. Fires ONLY for freshly-
                created doctors whose `db.doctors.onboarded_at` is null.
                Walks them through 3 essentials (photo → story → public
                phone) and stamps `onboarded_at` on any exit path so it
                truly never re-appears (backend-gated — survives cache
                clears and cross-device). No modal for existing doctors. */}
            <FirstLoginWizard
                doctor={doctorProfile}
                onComplete={() => api.get("/doctor/profile").then((r) => setDoctorProfile(r.data)).catch(() => {})}
            />

            {/* iter100.178 — Priority alert stack. Business-hours nudge
                still lives here (see DashboardAlertStack). */}
            <DashboardAlertStack doctorProfile={doctorProfile} />

            {/* iter100.261 — Lifecycle/trust widget. Shows completeness
                progress + go-live control. Only renders once we have the
                doctor's onboarding data (widget self-hides if fetch fails). */}
            <DoctorOnboardingWidget />

            {/* iter100.73 — Content-freshness card moved into the left
                column of the main grid, placed between the Compliance
                section and Recent activity. See below. */}

            {/* ── Page header ── matches /doctor/cases + /doctor/patients */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">
                        {firstName ? `Hello, Dr. ${firstName} 👋` : "Dashboard"}
                    </h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        {formatTodayLine()} · Here&apos;s what&apos;s waiting on you today.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        size="sm"
                        className="rounded-full"
                        onClick={() => navigate("/doctor/cases/new")}
                        data-testid="dashboard-new-case-btn"
                    >
                        <Plus size={14} className="mr-1" weight="bold" /> New Case
                    </Button>
                </div>
            </div>

            {/* iter100.180 — NeedsMeNow moved to the top-of-page slot
                (was previously in the left column of the main grid).
                Reasoning: "case actions waiting on you" is the single
                most important thing a doctor should see when they load
                the dashboard — it now sits above the KPI strip so it's
                the first visible surface after the priority alerts.
                LiveOffersBanner moved down to below Stay Visible Online.
                iter100.201 — Day-1 doctor sees a rewritten empty-state
                copy that reflects "your inbox will populate" instead of
                misleading "everything's caught up" grammar. */}
            <NeedsMeNow isDay1={isDay1} onNewCase={() => navigate("/doctor/cases/new")} />

            {/* iter100.184 — 4th snapshot card added (Compliance / CoE
                Coach). Grid widens to 4 columns at lg breakpoint. Card
                self-hides via conditional render when the doctor isn't
                subscribed to CoE-Support (has_service=false) — grid
                gracefully falls back to 3-across without a gap.
                iter100.185 — Section heading + tinted per-tone card
                backgrounds so the strip reads as an intentional
                dashboard surface, not a floating row of tiles. */}
            <div className="space-y-3">
                <SectionHeader
                    title="Your Daily Focus"
                    subtitle="Four essential indicators for your day"
                    testid="snapshot-strip-heading"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-stat-strip">
                <NestedStatCard
                    testid="snap-cases"
                    label="Cases"
                    Icon={Briefcase}
                    iconTone="primary"
                    rows={[
                        {
                            testid: "snap-cases-active",
                            name: "Active cases",
                            value: activeCasesCount,
                            sub: inProductionCount > 0 ? `${inProductionCount} in production` : null,
                            to: "/doctor/cases?stage=all",
                        },
                        {
                            testid: "snap-cases-with-team",
                            name: "With Invisique team",
                            value: withTeamCount,
                            sub: withTeamCount > 0 ? "Planning in progress" : null,
                            emphasise: withTeamCount > 0 ? "amber" : null,
                            to: "/doctor/cases?stage=in-planning",
                        },
                        {
                            testid: "snap-cases-pending",
                            name: "Pending review",
                            value: pendingReviewCount,
                            sub: pendingReviewCount > 0 ? "Ball in your court" : null,
                            emphasise: pendingReviewCount > 0 ? "amber" : null,
                            to: "/doctor/cases?stage=doctor-review",
                        },
                    ]}
                />
                <NestedStatCard
                    testid="snap-appts"
                    label="Appointments"
                    Icon={Calendar}
                    iconTone="amber"
                    rows={[
                        {
                            testid: "snap-appts-today",
                            name: "Today",
                            value: todayAppts.length,
                            sub: nextTodayAppt
                                ? `Next at ${(nextTodayAppt.time || "").slice(0, 5)}`
                                : todayAppts.length > 0 ? "All seen" : "Nothing scheduled",
                            to: "/doctor/appointments",
                        },
                        {
                            testid: "snap-appts-week",
                            name: "This week",
                            value: weekAppts,
                            sub: weekAppts > 0 ? "Next 7 days" : "Calendar clear",
                            to: "/doctor/appointments",
                        },
                        {
                            testid: "snap-appts-scan-visits",
                            name: "Scan visits booked",
                            value: scanVisitsCount,
                            sub: scanVisitsCount > 0
                                ? "IO scan pickups scheduled"
                                : "No scan visits booked",
                            to: "/doctor/cases?stage=intake",
                        },
                    ]}
                />
                <NestedStatCard
                    testid="snap-collections"
                    label="Collections"
                    Icon={Wallet}
                    iconTone="emerald"
                    rows={[
                        {
                            testid: "snap-collections-today",
                            name: "Collected today",
                            value: fmtINR(todaySummary?.collected_today?.amount || 0),
                            sub: (todaySummary?.collected_today?.invoice_count || 0) > 0
                                ? `${todaySummary.collected_today.invoice_count} invoice${todaySummary.collected_today.invoice_count === 1 ? "" : "s"}`
                                : "No payments yet",
                            to: "/doctor/billing",
                        },
                        {
                            testid: "snap-collections-ar",
                            name: "Outstanding A/R",
                            value: fmtINR(patientOutstanding.total),
                            sub: patientOutstanding.patients.length > 0
                                ? `Across ${patientOutstanding.patients.length} patient${patientOutstanding.patients.length === 1 ? "" : "s"}`
                                : "All balances cleared",
                            emphasise: patientOutstanding.total > 0 ? "rose" : null,
                            to: "/doctor/patients?balance=open",
                        },
                    ]}
                />
                {/* iter100.184 — 4th snapshot card: CoE Compliance Coach.
                    iter100.232 — Card now ALWAYS renders so the "Four
                    essential indicators" strip is complete for Day-1
                    doctors and doctors who haven't opted any case into
                    CoE-Support monitoring yet.
                    iter100.234 — Copy corrected: CoE-Support monitoring
                    is a PER-CASE toggle set in the New Case wizard, not
                    a service to subscribe to. Empty-state rows now
                    mirror the live variant (labels + testids) so the
                    UX stays consistent whether the doctor has zero or
                    many monitored cases. */}
                {(() => {
                    const hasService = !!coeSummary?.has_service;
                    if (!hasService) {
                        return (
                            <NestedStatCard
                                testid="snap-compliance"
                                label="Compliance Coach"
                                Icon={Headset}
                                iconTone="primary"
                                rows={[
                                    {
                                        testid: "snap-compliance-escalations",
                                        name: "Open escalations",
                                        value: 0,
                                        sub: "No monitored cases yet",
                                        to: "/doctor/escalations",
                                    },
                                    {
                                        testid: "snap-compliance-coverage",
                                        name: "Monitored",
                                        value: 0,
                                        sub: "Opt in per case when submitting",
                                        to: "/doctor/escalations",
                                    },
                                ]}
                            />
                        );
                    }
                    const openCount = coeSummary.open_escalations?.count || 0;
                    const newestAt = coeSummary.open_escalations?.newest_created_at;
                    const cov = coeSummary.coverage || { monitored: 0, total: 0 };
                    const notMonitored = Math.max(0, (cov.total || 0) - (cov.monitored || 0));
                    return (
                        <NestedStatCard
                            testid="snap-compliance"
                            label="Compliance Coach"
                            Icon={Headset}
                            iconTone={openCount > 0 ? "rose" : "primary"}
                            rows={[
                                {
                                    testid: "snap-compliance-escalations",
                                    name: "Open escalations",
                                    value: openCount,
                                    sub: openCount > 0
                                        ? `Newest ${relativeIso(newestAt)}`
                                        : "Coach hasn't flagged anything",
                                    emphasise: openCount > 0 ? "rose" : null,
                                    to: "/doctor/escalations",
                                },
                                {
                                    testid: "snap-compliance-coverage",
                                    name: "Monitored",
                                    value: cov.total > 0
                                        ? `${cov.monitored} / ${cov.total}`
                                        : "—",
                                    sub: notMonitored > 0
                                        ? `${notMonitored} not opted in`
                                        : cov.total > 0 ? "All active cases" : "No active cases",
                                    emphasise: notMonitored > 0 ? "amber" : null,
                                    to: notMonitored > 0 ? "/doctor/cases?monitoring=off" : "/doctor/cases",
                                },
                            ]}
                        />
                    );
                })()}
                </div>
            </div>

            {/* PLANNER QUESTIONS — now rendered inside DashboardAlertStack
                above (iter100.178). The old inline banner used to live
                here between the stat strip and the main grid; it moved
                up so all attention-requiring items surface together at
                the top of the page. */}

            {/* ── Main grid ── left action surface + right rail */}
            <div className="grid lg:grid-cols-[1fr_320px] gap-6">

                {/* ═════════════ LEFT COLUMN ═════════════ */}
                <div className="space-y-6 min-w-0">
                    {/* Scans waiting on doctor's plan — self-hides on quiet days */}
                    <ScansUploadedNudge cases={cases} />

                    {/* iter100.189 — Section renamed per user direction:
                        "Compliance — needs attention" → "Patient Compliance
                        Snapshot". Subtitle rewritten to match ("Track wear
                        patterns and compliance across all active treatments").
                        Internal `mt-3` spacers replaced with a `space-y-3`
                        wrapper for a consistent rhythm.
                        iter100.192 — "View full insights" right-slot link
                        removed per user direction. */}
                    <section data-testid="patient-nudges-section">
                        <SectionHeader
                            title="Patient Compliance Snapshot"
                            subtitle="Track wear patterns and compliance across all active treatments"
                        />
                        {isDay1 ? (
                            <ComplianceDay1Empty onNewCase={() => navigate("/doctor/cases/new")} />
                        ) : (
                            <div className="space-y-3">
                                <ComplianceStrip cases={cases} />
                                <ComplianceDigestCard />
                                <PatientNudges />
                            </div>
                        )}
                    </section>

                    {/* iter100.73 — Content-freshness card moved here from the
                        top-of-page position per user direction. Sits between
                        the Compliance section and Recent activity so the
                        doctor sees clinical action → marketing gaps → activity
                        feed in a natural top-to-bottom scan. */}
                    {contentFreshness && doctorProfile && doctorProfile.onboarded_at && (
                        <section data-testid="content-freshness-card">
                            <SectionHeader
                                title="Stay Visible Online"
                                subtitle="Your practice microsite is your 24/7 marketing tool."
                                rightSlot={
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => navigate("/doctor/landing-page")}
                                        className="rounded-full text-xs"
                                        data-testid="content-freshness-manage-btn"
                                    >
                                        Manage Microsite
                                    </Button>
                                }
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {[
                                    { key: "testimonials", label: "Reviews",      tabId: "reviews",    emptyCta: "Add first review" },
                                    { key: "articles",     label: "Articles",     tabId: "article",    emptyCta: "Write first article" },
                                    { key: "case_studies", label: "Case Studies", tabId: "case_study", emptyCta: "Publish first case study" },
                                ].map((t) => {
                                    const s = contentFreshness[t.key] || { count: 0, days_since: null };
                                    const isEmpty = s.count === 0;
                                    const isStale = !isEmpty && s.days_since != null && s.days_since > 60;
                                    const needsWork = isEmpty || isStale;
                                    // iter100.189 — Phrasing unified to "Last added …"
                                    // across all 3 states for parallel construction.
                                    const detail = isEmpty
                                        ? t.emptyCta
                                        : isStale
                                            ? `Last added ${s.days_since} days ago · consider refreshing`
                                            : s.days_since === 0
                                                ? "Last added today"
                                                : `Last added ${s.days_since} day${s.days_since === 1 ? "" : "s"} ago`;
                                    return (
                                        <button
                                            key={t.key}
                                            type="button"
                                            onClick={() => navigate(`/doctor/landing-page?tab=${t.tabId}`)}
                                            className={`text-left p-4 rounded-xl border transition-colors ${needsWork
                                                ? "border-amber-200 bg-amber-50/60 hover:border-amber-400 hover:bg-amber-50"
                                                : "border-border bg-secondary/30 hover:border-primary/40 hover:bg-primary/5"
                                            }`}
                                            data-testid={`content-freshness-tile-${t.key}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.label}</div>
                                                {needsWork && <Warning size={12} weight="duotone" className="text-amber-600" />}
                                            </div>
                                            <div className="mt-1.5 flex items-baseline gap-1.5">
                                                <span className={`font-display text-3xl font-bold tabular-nums leading-none ${isEmpty ? "text-amber-700" : "text-slate-900"}`}>
                                                    {s.count}
                                                </span>
                                                <span className="text-xs text-muted-foreground">published</span>
                                            </div>
                                            <div className={`mt-1.5 text-[12px] leading-snug ${needsWork ? "text-amber-800 font-medium" : "text-muted-foreground"}`}>
                                                {detail}
                                                {isEmpty && (
                                                    <span className="ml-1 opacity-80" aria-hidden>→</span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* iter100.181 — LiveOffersBanner removed from the
                        dashboard per user direction. Live promotions still
                        surface in the case wizard (auto-suggest promo
                        chips) and on the dedicated /doctor/offers page. */}

                    {/* Recent activity — derived client-side from cases + appts */}
                    <RecentActivity cases={cases} appts={appts} b2bInvoices={b2bInvoices} />
                </div>

                {/* ═════════════ RIGHT COLUMN (sticky) ═════════════
                    iter100.187 — Today's Schedule removed per user
                    direction (appointments already surface in the
                    snapshot strip's Appointments card + the Recent
                    activity feed in the left column). Widgets grouped
                    under 3 subtle section headings that mirror the
                    main-column layout's H2 spacing (space-y-6 between
                    sections) so the rail reads as intentional. */}
                <aside className="space-y-6">
                    {/* iter100.200 — Vertical offset at the lg breakpoint
                        so the sidebar's first eyebrow ("SHORTCUTS") aligns
                        with the top of the "Patient Compliance Snapshot"
                        strip on the left — matching the height of the
                        left column's SectionHeader (H2 + subtitle + mb-3).
                        Only applied at ≥lg where the two-column grid is
                        active; mobile stack is unaffected. */}
                    <div className="lg:sticky lg:top-24 space-y-6 lg:mt-[52px]">
                        <RailSection label="Shortcuts" testid="rail-section-shortcuts">
                            <QuickActionsCard navigate={navigate} />
                        </RailSection>
                        <RailSection
                            label="Pipeline"
                            testid="rail-section-pipeline"
                            rightSlot={
                                <Link to="/doctor/cases?stage=all" className="text-[10px] font-semibold text-primary hover:underline" data-testid="rail-pipeline-view-all">
                                    View all
                                </Link>
                            }
                        >
                            <InProductionCard cases={cases} isDay1={isDay1} onNewCase={() => navigate("/doctor/cases/new")} />
                        </RailSection>
                        <RailSection label="Money" testid="rail-section-money">
                            <InvisiqueInvoicesCard data={invisiqueOwed} isDay1={isDay1} />
                        </RailSection>
                    </div>
                </aside>
            </div>
        </div>
    );
}


/* ─────────── Nested-metric snapshot card (iter100.179) ───────────
   Two nested metric rows per card. Each row is an independent Link so
   the doctor can drill into either sub-metric directly (e.g. "Pending
   review" jumps to /doctor/cases?stage=doctor-review, not the whole
   Cases page). The card header carries one icon chip; row emphasis is
   done via optional per-row tone tokens. */
function NestedStatCard({ testid, label, Icon, iconTone = "primary", rows }) {
    /* iter100.189 — Icon-chip backgrounds bumped to `-200` shades (from
       `-100`) so the chip stands off cleanly against the tinted card
       gradient. Previously the primary chip on the teal-tinted CASES
       card blended into the background — visual weight now matches the
       amber/rose/emerald siblings. */
    const TONE_BG = {
        primary:  "bg-primary/20 text-primary",
        amber:    "bg-amber-200/80 text-amber-700",
        rose:     "bg-rose-200/80 text-rose-700",
        emerald:  "bg-emerald-200/80 text-emerald-700",
        indigo:   "bg-indigo-200/80 text-indigo-700",
        neutral:  "bg-secondary text-muted-foreground",
    };
    /* iter100.185 — Card tint per tone. Subtle gradient from a tinted
       top-left to the neutral card background, plus a matching border
       accent, so the strip reads as 4 distinct surfaces at a glance
       without shouting. iter100.186 — Cases tint switched from the
       CSS-var `primary/8` (which resolves near-invisible on white) to
       hardcoded teal-100/60 so it matches the visual weight of the
       amber/rose/indigo siblings. */
    const TONE_CARD = {
        primary:  "border-teal-300 bg-gradient-to-br from-teal-100/70 via-teal-50/40 to-card",
        amber:    "border-amber-200 bg-gradient-to-br from-amber-100/60 via-amber-50/30 to-card",
        rose:     "border-rose-200 bg-gradient-to-br from-rose-100/60 via-rose-50/30 to-card",
        emerald:  "border-emerald-200 bg-gradient-to-br from-emerald-100/60 via-emerald-50/30 to-card",
        indigo:   "border-indigo-200 bg-gradient-to-br from-indigo-100/60 via-indigo-50/30 to-card",
        neutral:  "border-border bg-card",
    };
    const emphColor = {
        rose:  "text-rose-700",
        amber: "text-amber-700",
    };
    return (
        <div
            data-testid={testid}
            className={`border rounded-xl p-4 flex flex-col gap-2 hover:shadow-md hover:-translate-y-0.5 transition-all ${TONE_CARD[iconTone] || TONE_CARD.neutral}`}
        >
            <div className="flex items-start justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    {label}
                </span>
                <span className={`w-7 h-7 rounded-lg ${TONE_BG[iconTone] || TONE_BG.neutral} grid place-items-center shrink-0`}>
                    <Icon size={14} weight="duotone" />
                </span>
            </div>
            <div className="divide-y divide-border/60 -mx-1">
                {rows.map((row) => (
                    <Link
                        key={row.testid}
                        to={row.to || "#"}
                        data-testid={row.testid}
                        className="group flex items-center justify-between gap-2 px-1 py-2 rounded-md hover:bg-secondary/40 transition-colors"
                    >
                        <div className="min-w-0">
                            <div className="text-[11px] text-muted-foreground truncate">{row.name}</div>
                            {row.sub && (
                                <div className="text-[10px] text-muted-foreground/70 truncate mt-0.5">{row.sub}</div>
                            )}
                        </div>
                        <div className={`font-display font-bold tabular-nums text-[18px] leading-none ${emphColor[row.emphasise] || "text-foreground"} group-hover:text-primary transition-colors`}>
                            {row.value}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}



/* ─────────── Compliance summary strip (above PatientNudges) ─────────── */
const COMPLIANCE_LIFECYCLE_STATUSES = new Set([
    "delivered", "in_treatment", "treatment_started", "refinement", "in_retention",
]);

function complianceBucket(c) {
    if (!COMPLIANCE_LIFECYCLE_STATUSES.has(c.status)) return null;
    const stage = c.current_aligner_stage || 0;
    if (stage === 0) return "pending";
    const ref = c.aligner_stage_updated_at || c.updated_at;
    if (!ref) return "pending";
    const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
    if (Number.isNaN(days)) return "pending";
    if (days <= 2) return "on-track";
    if (days <= 7) return "at-risk";
    return "needs-attention";
}


function ComplianceStrip({ cases }) {
    const buckets = useMemo(() => {
        const out = { "on-track": 0, "at-risk": 0, "needs-attention": 0, "pending": 0 };
        for (const c of cases) {
            const b = complianceBucket(c);
            if (b && b in out) out[b] += 1;
        }
        return out;
    }, [cases]);
    const totalActive = buckets["on-track"] + buckets["at-risk"] + buckets["needs-attention"];
    if (totalActive === 0 && buckets.pending === 0) return null;

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="compliance-strip">
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border">
                <ComplianceTile testid="compliance-on-track" bucket="on-track" count={buckets["on-track"]} label="On track" sub="Wearing as prescribed" tone="primary" />
                <ComplianceTile testid="compliance-mild-delay" bucket="at-risk" count={buckets["at-risk"]} label="Mild delay" sub="3–7 days late" tone="amber" />
                <ComplianceTile testid="compliance-stalled" bucket="needs-attention" count={buckets["needs-attention"]} label="Stalled" sub=">7 days late" tone="rose" />
                <ComplianceTile testid="compliance-pending" bucket="pending" count={buckets["pending"]} label="Awaiting fit" sub="Stage 0" tone="neutral" />
            </div>
        </div>
    );
}


function ComplianceTile({ testid, bucket, count, label, sub, tone }) {
    const numberColor = {
        primary: "text-primary",
        amber:   "text-amber-700",
        rose:    "text-rose-700",
        neutral: "text-muted-foreground",
    }[tone] || "text-foreground";
    return (
        <Link
            to={`/doctor/cases?view=compliance&compliance=${bucket}`}
            className="p-4 hover:bg-secondary/40 transition-colors block"
            data-testid={testid}
        >
            <div className={`font-display text-[26px] font-bold tabular-nums leading-none ${numberColor}`}>{count}</div>
            <div className="text-xs font-semibold mt-2 text-foreground">{label}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{sub}</div>}
        </Link>
    );
}


/* ─────────── Day-1 empty state for the Compliance section (iter100.199)
   Replaces the ComplianceStrip + ComplianceDigestCard + PatientNudges
   stack when the doctor has zero real cases. Stacking two green "all
   clear" cards on a brand-new account was misleading (implied active
   monitoring where there is none) — this single muted card explains
   what compliance monitoring IS and how it turns on.               */
function ComplianceDay1Empty({ onNewCase }) {
    const bullets = [
        { Icon: Sparkle,       text: "Daily wear-time tracking once your patient starts wearing aligners" },
        { Icon: HeartStraight, text: "Automatic risk buckets (on track / mild delay / stalled) as data flows in" },
        { Icon: Warning,       text: "Nudge-ready patient list the moment a compliance issue shows up" },
    ];
    return (
        <div
            className="bg-card border border-dashed border-border rounded-xl p-5 sm:p-6"
            data-testid="compliance-empty-day1"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                    <HeartStraight size={18} weight="duotone" className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-[15px] leading-tight">
                        Compliance monitoring turns on with your first case
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 leading-snug">
                        Once a patient starts treatment you&apos;ll see wear-time buckets, drift alerts, and one-tap nudge lists — all in this space.
                    </div>
                </div>
            </div>
            <ul className="mt-4 space-y-2 pl-1" data-testid="compliance-empty-day1-bullets">
                {bullets.map(({ Icon, text }) => (
                    <li key={text} className="flex items-start gap-2 text-[12px] text-muted-foreground">
                        <Icon size={13} weight="duotone" className="text-primary/70 mt-0.5 shrink-0" />
                        <span className="leading-snug">{text}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Button
                    size="sm"
                    className="rounded-full h-8 px-3.5 text-xs"
                    onClick={onNewCase}
                    data-testid="compliance-empty-day1-cta"
                >
                    <Plus size={12} className="mr-1" weight="bold" /> Start your first case
                </Button>
                <Link
                    to="/doctor/patients?add=1"
                    className="text-[11px] font-semibold text-primary hover:underline"
                    data-testid="compliance-empty-day1-add-patient"
                >
                    or add a patient first →
                </Link>
            </div>
        </div>
    );
}


/* ─────────── Right-rail widgets ─────────── */

function QuickActionsCard({ navigate }) {
    // iter100.189 — Quick actions rewired per user direction (2026-02-28):
    // New Patient → New Case → New Appt → Refer. Lab Chat dropped from
    // this rail card; the same surface remains reachable from the sidebar
    // and from the Needs-me-now inbox. "New Patient" deep-links to the
    // Patients page with `?add=1` which auto-opens the Add Patient dialog.
    const items = [
        { Icon: UserPlus,     label: "New Patient", to: () => navigate("/doctor/patients?add=1"), testid: "qa-new-patient" },
        { Icon: FilePlus,     label: "New Case",    to: () => navigate("/doctor/cases/new"),      testid: "qa-new-case" },
        { Icon: CalendarPlus, label: "New Appt",    to: () => navigate("/doctor/appointments"),   testid: "qa-book-appt" },
        { Icon: ShareNetwork, label: "Refer",       to: () => navigate("/doctor/profile?tab=refer"), testid: "qa-refer" },
    ];
    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="quick-actions-card">
            <div className="grid grid-cols-2 divide-x divide-border">
                {items.map((it, i) => (
                    <button
                        key={it.label}
                        onClick={it.to}
                        className={`flex flex-col items-center justify-center gap-1.5 py-4 hover:bg-secondary/40 transition-colors ${i < 2 ? "border-b border-border" : ""}`}
                        data-testid={it.testid}
                    >
                        <it.Icon size={20} weight="duotone" className="text-primary" />
                        <span className="text-[11px] font-medium text-foreground/80">{it.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}


function InProductionCard({ cases, isDay1, onNewCase }) {
    const items = useMemo(() => {
        return cases
            .filter((c) => IN_PRODUCTION_STATUSES.has(c.status))
            .sort((a, b) => new Date(b.status_updated_at || 0).getTime() - new Date(a.status_updated_at || 0).getTime())
            .slice(0, 4);
    }, [cases]);
    if (items.length === 0) {
        // iter100.199 — Populate-in-place empty state for Day 1. Ongoing
        // doctors with no active in-production items still see the rail
        // section collapse (null return below) — they know what "in
        // production" means and an empty card would just be noise.
        if (isDay1) {
            return (
                <button
                    type="button"
                    onClick={onNewCase}
                    className="w-full text-left bg-card border border-dashed border-border rounded-xl p-4 hover:border-primary/40 hover:bg-secondary/30 transition-colors group"
                    data-testid="in-production-empty-day1"
                >
                    <div className="flex items-start gap-2.5">
                        <span className="w-8 h-8 rounded-lg bg-secondary text-muted-foreground grid place-items-center shrink-0">
                            <Factory size={14} weight="duotone" />
                        </span>
                        <div className="min-w-0">
                            <div className="text-[12px] font-semibold text-foreground/85">Your lab pipeline will live here</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                                Once a case ships to the lab, you&apos;ll see progress at a glance.
                            </div>
                            <div className="text-[11px] text-primary font-semibold mt-2 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                                Start your first case <ArrowRight size={11} />
                            </div>
                        </div>
                    </div>
                </button>
            );
        }
        // iter101.78 — Ongoing doctors with no in-production items USED
        // to see `return null` here, which left the "PIPELINE" rail
        // header floating alone with no content beneath. That reads as
        // broken UI ("are we not fetching anything?"). Show a subtle
        // "all quiet" empty state instead so the header always has a
        // sibling. Same treatment for InvisiqueInvoicesCard below.
        return (
            <div
                className="bg-card border border-dashed border-border/70 rounded-xl p-4"
                data-testid="in-production-empty-ongoing"
            >
                <div className="flex items-start gap-2.5">
                    <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
                        <Factory size={14} weight="duotone" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[12px] font-semibold text-foreground/85">Lab is quiet right now</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            Nothing in production this moment — new cases will surface here the second they ship.
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="in-production-card">
            <ul className="divide-y divide-border">
                {items.map((c) => {
                    const sub = sharedStatusLabel(c.status, "doctor") || c.status?.replace(/_/g, " ");
                    return (
                        <li key={c.id}>
                            <Link
                                to={`/doctor/cases/${c.id}`}
                                className="flex items-start gap-2 px-4 py-2.5 hover:bg-secondary/30 transition-colors"
                                data-testid={`in-prod-${c.id}`}
                            >
                                <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                                <span className="flex-1 min-w-0">
                                    <span className="block font-medium text-[13px] truncate">
                                        {c.patient_name || "Patient"} · <span className="font-mono text-[11px] text-muted-foreground">{c.code}</span>
                                    </span>
                                    <span className="block text-[10px] text-muted-foreground">{sub}</span>
                                </span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}


function InvisiqueInvoicesCard({ data, isDay1 }) {
    if (!data || data.count === 0) {
        // iter100.199 — Day-1 muted empty state. Ongoing doctors who
        // have paid every invoice USED to hit the null-return branch
        // (rail collapses). iter101.78 — Now they see a subtle "all
        // settled" card so the "MONEY" rail label always has a
        // sibling.
        if (isDay1) {
            return (
                <div
                    className="bg-card border border-dashed border-border rounded-xl p-4"
                    data-testid="invisique-invoices-empty-day1"
                >
                    <div className="flex items-start gap-2.5">
                        <span className="w-8 h-8 rounded-lg bg-secondary text-muted-foreground grid place-items-center shrink-0">
                            <Receipt size={14} weight="duotone" />
                        </span>
                        <div className="min-w-0">
                            <div className="text-[12px] font-semibold text-foreground/85">Invisique&trade; invoices</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                                You&apos;ll be billed after cases ship. Nothing outstanding right now.
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div
                className="bg-card border border-dashed border-border/70 rounded-xl p-4"
                data-testid="invisique-invoices-empty-ongoing"
            >
                <div className="flex items-start gap-2.5">
                    <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
                        <Receipt size={14} weight="duotone" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-[12px] font-semibold text-foreground/85">You&apos;re all settled</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            No Invisique&trade; invoices to pay. Fresh bills will show up here after your next lab shipment.
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    const hasOverdue = data.overdueCount > 0;
    return (
        <Link
            to="/doctor/billing?tab=invisique"
            className="block bg-card border border-border hover:border-primary/30 rounded-xl p-4 transition-colors group"
            data-testid="invisique-invoices-card"
        >
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <Receipt size={13} weight="duotone" className={hasOverdue ? "text-rose-700" : "text-primary"} />
                Invisique™ invoices to settle
            </div>
            <div className="mt-2 flex items-baseline gap-2">
                <span className={`font-display text-[22px] font-bold tabular-nums leading-none ${hasOverdue ? "text-rose-900" : "text-foreground"}`}>
                    {fmtINR(data.total)}
                </span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5">
                {data.count} invoice{data.count === 1 ? "" : "s"} open
                {hasOverdue && <span className="text-rose-700 font-semibold ml-1">· {data.overdueCount} overdue</span>}
            </div>
            <div className="text-[11px] text-primary font-semibold mt-3 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                Open billing <ArrowRight size={11} />
            </div>
        </Link>
    );
}


/* ─────────── Recent activity (derived client-side) ─────────── */
function RecentActivity({ cases, appts, b2bInvoices }) {
    const items = useMemo(() => {
        const out = [];
        // Recent case status updates — skip drafts and cases without a
        // patient name (those are usually empty stubs not worth surfacing).
        for (const c of cases.slice().sort((a, b) =>
            new Date(b.status_updated_at || b.updated_at || 0).getTime() -
            new Date(a.status_updated_at || a.updated_at || 0).getTime()
        ).slice(0, 12)) {
            const t = c.status_updated_at || c.updated_at;
            if (!t) continue;
            if (c.status === "draft" || c.status === "cancelled") continue;
            if (!c.patient_name) continue;
            const label = sharedStatusLabel(c.status, "doctor") || (c.status || "").replace(/_/g, " ");
            out.push({
                kind: "case",
                tone: c.status === "sent_for_approval" ? "amber"
                    : c.status === "info_requested" ? "amber"
                    : c.status === "shipped" || c.status === "in_treatment" ? "primary"
                    : "neutral",
                at: t,
                key: `case-${c.id}-${t}`,
                href: `/doctor/cases/${c.id}`,
                text: (
                    <>
                        <strong>{c.patient_name || "Patient"}</strong> case moved to <em className="not-italic font-medium">{label}</em> · <span className="font-mono text-[10px]">{c.code}</span>
                    </>
                ),
            });
        }
        // Recent appointment bookings
        for (const a of appts.slice().sort((a, b) =>
            new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        ).slice(0, 4)) {
            if (!a.created_at) continue;
            out.push({
                kind: "appt",
                tone: "indigo",
                at: a.created_at,
                key: `appt-${a.id}`,
                href: `/doctor/appointments/${a.id}`,
                text: (
                    <>
                        <strong>{a.patient_name || "Patient"}</strong> booked a <em className="not-italic font-medium capitalize">{(a.appointment_type || "appointment").replace(/_/g, " ")}</em> · {a.date} {(a.time || "").slice(0, 5)}
                    </>
                ),
            });
        }
        // Recently overdue invoices
        for (const inv of b2bInvoices.filter((i) => i.status === "overdue").slice(0, 2)) {
            if (!inv.updated_at) continue;
            out.push({
                kind: "invoice",
                tone: "rose",
                at: inv.updated_at,
                key: `inv-${inv.id}`,
                href: "/doctor/billing?tab=invisique",
                text: (
                    <>
                        Invoice <strong>{inv.code || inv.id}</strong> is now overdue — {fmtINR(inv.balance)}
                    </>
                ),
            });
        }
        return out
            .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
            .slice(0, 6);
    }, [cases, appts, b2bInvoices]);

    if (items.length === 0) return null;
    const dotColor = (tone) => ({
        primary: "bg-primary",
        amber:   "bg-amber-500",
        rose:    "bg-rose-500",
        indigo:  "bg-indigo-500",
        neutral: "bg-muted-foreground/60",
    }[tone] || "bg-muted-foreground/60");

    return (
        <section data-testid="recent-activity-section">
            <SectionHeader
                title="Recent activity"
                subtitle="Last 24 hours across your cases, patients and billing"
            />
            <div className="bg-card border border-border rounded-xl overflow-hidden">
                <ul className="divide-y divide-border">
                    {items.map((it, idx) => (
                        <li key={it.key}>
                            <Link
                                to={it.href}
                                className="flex items-start gap-3 px-4 py-3 hover:bg-secondary/30 transition-colors"
                                data-testid={`activity-row-${idx}`}
                            >
                                <div className="flex flex-col items-center pt-1 shrink-0">
                                    <span className={`w-2 h-2 rounded-full ${dotColor(it.tone)}`} />
                                </div>
                                <div className="text-[12px] text-muted-foreground leading-snug flex-1 min-w-0">
                                    {it.text}
                                </div>
                                <span className="text-[10px] text-muted-foreground/80 whitespace-nowrap shrink-0">
                                    {timeAgo(it.at)}
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}


/* ─────────── Small reusable bits ─────────── */
/* iter100.187 — Section header for the right rail. Small uppercase
   eyebrow with a subtle divider trailing to the right — mirrors the
   heading style used above the snapshot strip so the sidebar reads as
   an intentional layout rather than a stack of unrelated widgets.
   iter100.189 — Extended with optional `rightSlot` so per-section
   right-side links (e.g. "View all" on PIPELINE) can render inline
   with the eyebrow instead of being buried inside a nested widget
   header inside the card. */
function RailSection({ label, testid, rightSlot, children }) {
    return (
        <div className="space-y-2.5" data-testid={testid}>
            <div className="flex items-center gap-2 px-0.5">
                <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground/80">
                    {label}
                </span>
                <span className="flex-1 h-px bg-border/70" />
                {rightSlot}
            </div>
            {children}
        </div>
    );
}


function SectionHeader({ title, subtitle, rightSlot, testid }) {
    return (
        <div className="flex items-end justify-between gap-3 mb-3">
            <div className="min-w-0">
                <h2 className="font-display text-lg font-bold tracking-tight" data-testid={testid}>{title}</h2>
                {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
            {rightSlot && <div className="shrink-0">{rightSlot}</div>}
        </div>
    );
}
